import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Check, ArrowLeft, Building } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { stripe as stripeApi, contact } from '../utils/api';
import Logo from '../components/Logo';
import SEO from '../components/SEO';

const Pricing = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(null);
  const [error, setError] = useState('');
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [enterpriseForm, setEnterpriseForm] = useState({ name: '', email: '', company: '', message: '' });
  const [enterpriseSuccess, setEnterpriseSuccess] = useState(false);
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherStatus, setVoucherStatus] = useState(null); // null | { valid, discountPercent, description } | { valid: false, error }
  const [validatingVoucher, setValidatingVoucher] = useState(false);

  const plans = [
    {
      key: 'single',
      name: 'Single Report',
      price: '29',
      period: 'one-time',
      reports: 1,
      features: [
        'One comprehensive due diligence report',
        'AI-powered research and analysis',
        'Risk assessment with scoring',
        'Downloadable Word/PDF format',
        'Source citations included',
        'The complete publicly available picture'
      ]
    },
    {
      key: 'starter',
      name: 'Starter',
      price: '150',
      period: 'month',
      reports: 10,
      features: [
        'Up to 10 reports per month',
        'Every feature included from Single Report',
        'Full report history and archive access',
        'Email support',
        'Manage multiple subjects with ease'
      ]
    },
    {
      key: 'professional',
      name: 'Professional',
      price: '499',
      period: 'month',
      reports: 50,
      popular: true,
      features: [
        'Up to 50 reports per month',
        'All Starter features included',
        'Priority email support',
        'Dedicated account manager',
        'Volume reporting across your organisation'
      ]
    }
  ];

  const handleApplyVoucher = async () => {
    if (!voucherCode.trim()) return;
    setValidatingVoucher(true);
    setVoucherStatus(null);
    try {
      const result = await stripeApi.validateVoucher(voucherCode.trim().toUpperCase());
      setVoucherStatus(result);
    } catch (err) {
      setVoucherStatus({ valid: false, error: err.message || 'Invalid voucher code' });
    } finally {
      setValidatingVoucher(false);
    }
  };

  const handlePurchase = async (planKey) => {
    setError('');
    setLoading(planKey);
    try {
      const appliedVoucher = voucherStatus?.valid ? voucherCode.trim().toUpperCase() : null;
      const result = await stripeApi.createCheckout(planKey, appliedVoucher);
      if (result.freeCheckout) {
        // 100% voucher — credits already added server-side. Refresh local
        // user state BEFORE navigating so ReportForm sees the new credit
        // count and doesn't bounce back to /pricing.
        await refreshUser();
        navigate('/reports/new', { state: { message: result.message || 'Credits added! You can now generate reports.' } });
      } else {
        window.location.href = result.url;
      }
    } catch (err) {
      setError(err.message || 'Failed to start checkout');
      setLoading(null);
    }
  };

  const handleEnterpriseSubmit = async (e) => {
    e.preventDefault();
    setLoading('enterprise');
    try {
      await contact.enterprise(enterpriseForm);
      setEnterpriseSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Pricing — id100"
        description="Choose a plan: single report at £29, monthly subscriptions for high-volume use, or enterprise. AI-powered due diligence reports with blockchain audit hashes."
        path="/pricing"
      />
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5" />
              Back to Dashboard
            </Link>
            <Logo size="sm" />
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900">Choose Your Plan</h1>
          <p className="mt-2 text-gray-600">Select the plan that best fits your due diligence needs</p>
        </div>

        {error && (
          <div className="max-w-md mx-auto mb-8 p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {/* Voucher Code Input */}
        <div className="max-w-md mx-auto mb-8">
          <div className="bg-white rounded-lg shadow p-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Have a voucher code?</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={voucherCode}
                onChange={(e) => { setVoucherCode(e.target.value.toUpperCase()); setVoucherStatus(null); }}
                placeholder="Enter voucher code"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm font-mono uppercase focus:ring-red-500 focus:border-red-500"
                onKeyDown={(e) => e.key === 'Enter' && handleApplyVoucher()}
              />
              <button
                onClick={handleApplyVoucher}
                disabled={validatingVoucher || !voucherCode.trim()}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {validatingVoucher ? 'Checking...' : 'Apply'}
              </button>
            </div>
            {voucherStatus && voucherStatus.valid && (
              <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md flex items-center gap-2">
                <Check className="h-4 w-4 text-green-600" />
                <span className="text-sm text-green-800 font-medium">
                  {voucherStatus.discountPercent}% discount applied!
                  {voucherStatus.description && ` — ${voucherStatus.description}`}
                </span>
              </div>
            )}
            {voucherStatus && !voucherStatus.valid && (
              <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                <span className="text-sm text-red-600">{voucherStatus.error || 'Invalid voucher code'}</span>
              </div>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {plans.map((plan) => (
            <div
              key={plan.key}
              className={`bg-white rounded-lg shadow-lg overflow-hidden ${
                plan.popular ? 'ring-2 ring-red-500' : ''
              }`}
            >
              {plan.popular && (
                <div className="bg-red-500 text-white text-center py-1 text-sm font-medium">
                  Most Popular
                </div>
              )}
              <div className="p-6">
                <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                <div className="mt-4">
                  {voucherStatus?.valid ? (
                    <>
                      <span className="text-2xl text-gray-400 line-through mr-2">£{plan.price}</span>
                      <span className="text-4xl font-bold text-green-600">
                        £{Math.round(plan.price * (1 - voucherStatus.discountPercent / 100))}
                      </span>
                    </>
                  ) : (
                    <span className="text-4xl font-bold text-gray-900">£{plan.price}</span>
                  )}
                  <span className="text-gray-500">/{plan.period}</span>
                </div>
                <p className="mt-2 text-sm text-gray-500">
                  {plan.reports} report{plan.reports > 1 ? 's' : ''} {plan.period === 'month' ? 'per month' : ''}
                </p>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => handlePurchase(plan.key)}
                  disabled={loading === plan.key}
                  className={`mt-8 w-full py-3 px-4 rounded-md text-sm font-medium transition ${
                    plan.popular
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-gray-100 text-gray-900 hover:bg-gray-200'
                  } disabled:opacity-50`}
                >
                  {loading === plan.key ? 'Processing...' : 'Get Started'}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Enterprise */}
        <div className="bg-gray-900 rounded-lg p-8 text-center">
          <Building className="h-12 w-12 text-white mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white">Enterprise</h3>
          <p className="text-gray-400 mt-1 text-sm">More than 50 reports per month</p>
          <p className="text-gray-300 mt-4 max-w-2xl mx-auto">
            Need higher volumes or custom integrations? Contact us for enterprise pricing tailored to your organisation's requirements.
          </p>
          <p className="text-gray-400 mt-2 text-sm italic">
            API and MCP access coming soon
          </p>
          <p className="text-xl font-semibold text-white mt-4">Price on Application</p>
          <button
            onClick={() => setShowEnterpriseModal(true)}
            className="mt-6 bg-white text-gray-900 px-8 py-3 rounded-md font-medium hover:bg-gray-100"
          >
            Contact Sales
          </button>
        </div>
      </main>

      {/* Enterprise Modal */}
      {showEnterpriseModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            {enterpriseSuccess ? (
              <div className="text-center">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
                <p className="text-gray-600 mb-4">We'll be in touch shortly.</p>
                <a
                  href="https://tidycal.com/totalinfo/dd-consultation"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:text-red-700 font-medium"
                >
                  Book a consultation →
                </a>
                <button
                  onClick={() => { setShowEnterpriseModal(false); setEnterpriseSuccess(false); }}
                  className="mt-4 w-full py-2 bg-gray-100 rounded-md"
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleEnterpriseSubmit}>
                <h3 className="text-xl font-bold text-gray-900 mb-4">Enterprise Enquiry</h3>
                <div className="space-y-4">
                  <input
                    type="text"
                    placeholder="Your Name"
                    required
                    value={enterpriseForm.name}
                    onChange={(e) => setEnterpriseForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  <input
                    type="email"
                    placeholder="Email"
                    required
                    value={enterpriseForm.email}
                    onChange={(e) => setEnterpriseForm(p => ({ ...p, email: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  <input
                    type="text"
                    placeholder="Company"
                    required
                    value={enterpriseForm.company}
                    onChange={(e) => setEnterpriseForm(p => ({ ...p, company: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                  <textarea
                    placeholder="Tell us about your requirements"
                    rows={3}
                    required
                    value={enterpriseForm.message}
                    onChange={(e) => setEnterpriseForm(p => ({ ...p, message: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-md"
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <button type="button" onClick={() => setShowEnterpriseModal(false)} className="flex-1 py-2 bg-gray-100 rounded-md">
                    Cancel
                  </button>
                  <button type="submit" disabled={loading === 'enterprise'} className="flex-1 py-2 bg-red-600 text-white rounded-md">
                    {loading === 'enterprise' ? 'Sending...' : 'Submit'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Pricing;
